import { z } from 'zod';
import { AgentStatus, LLMProvider } from '../enums';

/**
 * Agent Configuration Schema
 */
export const AgentConfigSchema = z.object({
  model: z.string(),
  provider: z.nativeEnum(LLMProvider),
  temperature: z.number().min(0).max(2).default(0.7),
  maxTokens: z.number().int().positive().default(2000),
  topP: z.number().min(0).max(1).default(0.9),
  systemPrompt: z.string().max(10000).optional(),
  enableRAG: z.boolean().default(true),
  enableTools: z.boolean().default(true),
  tools: z.array(z.string()).optional(),
});

export type AgentConfig = z.infer<typeof AgentConfigSchema>;

/**
 * Agent DTO Schema
 */
export const AgentSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  name: z.string().min(1).max(255),
  description: z.string().max(1000).nullable().optional(),
  status: z.nativeEnum(AgentStatus).default(AgentStatus.DRAFT),
  config: AgentConfigSchema,
  createdBy: z.string().uuid(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type Agent = z.infer<typeof AgentSchema>;

/**
 * Create Agent DTO Schema (for API requests)
 * businessId and createdBy are optional in the request body as they are extracted from JWT
 */
export const CreateAgentSchema = z.object({
  businessId: z.string().uuid().optional(),
  name: z.string().min(1).max(255),
  description: z.string().max(1000).optional(),
  config: AgentConfigSchema,
  createdBy: z.string().uuid().optional(),
});

export type CreateAgent = z.infer<typeof CreateAgentSchema>;

/**
 * Update Agent DTO Schema
 */
export const UpdateAgentSchema = z
  .object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().max(1000).nullable().optional(),
    status: z.nativeEnum(AgentStatus).optional(),
    config: AgentConfigSchema.partial().optional(),
  })
  .refine((data) => Object.keys(data).length > 0, {
    message: 'At least one field must be provided for update',
  });

export type UpdateAgent = z.infer<typeof UpdateAgentSchema>;

/**
 * Agent Invocation Message Schema
 */
export const AgentMessageSchema = z.object({
  role: z.enum(['user', 'assistant', 'system']),
  content: z.string().min(1),
  timestamp: z.date().optional(),
});

export type AgentMessage = z.infer<typeof AgentMessageSchema>;

/**
 * Agent Invocation Request Schema
 */
export const InvokeAgentSchema = z.object({
  message: z.string().min(1).max(10000),
  conversationId: z.string().uuid().optional(),
  stream: z.boolean().default(true),
  includeContext: z.boolean().default(true),
  maxTokens: z.number().int().positive().optional(),
  /** Source portal for knowledge base scoping (business, consumer, admin, auth) */
  portal: z.enum(['business', 'consumer', 'admin', 'auth']).optional(),
  /**
   * Issue #1434 — fork-from-message. When set, the new user message is
   * persisted with `parentMessageId = fromMessageId`, allowing the request
   * to branch from an earlier message (used by assistant-ui's edit-and-
   * resubmit and regenerate flows).
   *
   * Current behavior: the field is forwarded/plumbed for branching-related
   * persistence and request handling. Follow-up work may use it to load or
   * truncate conversation history to the ancestor chain of `fromMessageId`,
   * but that should not be assumed by API consumers yet.
   */
  fromMessageId: z.string().uuid().optional(),
});

export type InvokeAgent = z.infer<typeof InvokeAgentSchema>;

/**
 * Agent Response Chunk Schema (for streaming)
 *
 * Issue #1432: Extended with `tool_call_start` and `tool_result` chunk types so
 * structured tool execution data reaches the frontend instead of being silently
 * dropped at the gateway. The agent service emits these via gRPC chunk_type
 * `TOOL_USE` / `TOOL_RESULT`; the API Gateway re-shapes them into the chunk
 * variants below before sending them down the SSE stream.
 */
export const AgentResponseChunkSchema = z.object({
  type: z.enum(['content', 'metadata', 'error', 'done', 'sources', 'tool_call_start', 'tool_result']),
  content: z.string().optional(),
  metadata: z
    .object({
      conversationId: z.string().uuid().optional(),
      tokensUsed: z.number().int().optional(),
      model: z.string().optional(),
      finishReason: z.string().optional(),
      promptTokens: z.number().int().optional(),
      completionTokens: z.number().int().optional(),
      totalCost: z.number().optional(),
      agentId: z.string().optional(),
      agentName: z.string().optional(),
      ragDocumentsFound: z.number().int().optional(),
      /** Issue #1434 — id of the user message just persisted (= parent of the assistant turn). */
      userMessageId: z.string().uuid().optional(),
      /** Issue #1434 — id of the assistant message persisted at end of stream. */
      assistantMessageId: z.string().uuid().optional(),
    })
    .optional(),
  sources: z
    .array(
      z.object({
        id: z.string(),
        content: z.string(),
        metadata: z.record(z.string(), z.unknown()).optional(),
        score: z.number().optional(),
      }),
    )
    .optional(),
  error: z.string().optional(),
  /**
   * Tool execution: identifier shared between a `tool_call_start` chunk and the
   * matching `tool_result` chunk so the UI can pair them up. For LLM-driven
   * tool calls this is the model's tool_call.id; for system-injected calls
   * (e.g. proactive RAG search, memory context load) it is a synthesized UUID.
   */
  toolCallId: z.string().optional(),
  /** Tool name, e.g. `search_knowledge_base`. */
  toolName: z.string().optional(),
  /** Parsed JSON arguments the LLM (or system) passed to the tool. */
  toolArgs: z.record(z.string(), z.unknown()).optional(),
  /**
   * Raw tool result data — NOT stringified. Whatever shape the tool returned,
   * forwarded as-is so the frontend can render structured UI from it.
   */
  data: z.unknown().optional(),
  /** Tool execution duration in milliseconds (set on `tool_result` chunks). */
  durationMs: z.number().int().nonnegative().optional(),
});

export type AgentResponseChunk = z.infer<typeof AgentResponseChunkSchema>;

/**
 * Strongly-typed shape for `tool_call_start` chunks. Useful for narrowing in
 * consumers that want non-optional access to `toolCallId` / `toolName`.
 */
export interface AgentToolCallStart {
  type: 'tool_call_start';
  toolCallId: string;
  toolName: string;
  toolArgs?: Record<string, unknown>;
}

/**
 * Strongly-typed shape for `tool_result` chunks. The `data` field is generic so
 * call sites can express the structured payload they expect from a given tool.
 */
export interface AgentToolResult<TData = unknown> {
  type: 'tool_result';
  toolCallId: string;
  toolName: string;
  data?: TData;
  error?: string;
  durationMs?: number;
}

/** Type guard for `tool_call_start` chunks. */
export function isToolCallStartChunk(chunk: AgentResponseChunk): chunk is AgentResponseChunk & AgentToolCallStart {
  return chunk.type === 'tool_call_start' && typeof chunk.toolCallId === 'string' && typeof chunk.toolName === 'string';
}

/** Type guard for `tool_result` chunks. */
export function isToolResultChunk<TData = unknown>(
  chunk: AgentResponseChunk,
): chunk is AgentResponseChunk & AgentToolResult<TData> {
  return chunk.type === 'tool_result' && typeof chunk.toolCallId === 'string' && typeof chunk.toolName === 'string';
}

// ============================================================================
// AGENT RESPONSE METADATA (Standalone)
// ============================================================================

/**
 * Agent Response Metadata Schema
 * Structured metadata returned from agent invocations
 * Used by portals for typing agent invoke responses
 */
export const AgentResponseMetadataSchema = z.object({
  conversationId: z.string().uuid().optional(),
  tokensUsed: z.number().int().nonnegative().optional(),
  model: z.string().optional(),
  finishReason: z.string().optional(),
  promptTokens: z.number().int().nonnegative().optional(),
  completionTokens: z.number().int().nonnegative().optional(),
  totalCost: z.number().nonnegative().optional(),
  agentId: z.string().optional(),
  agentName: z.string().optional(),
  ragDocumentsFound: z.number().int().nonnegative().optional(),
  latencyMs: z.number().int().nonnegative().optional(),
  toolsUsed: z.array(z.string()).optional(),
});

export type AgentResponseMetadata = z.infer<typeof AgentResponseMetadataSchema>;
